import React from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  useForm,
  UseFormProps,
  FieldValues,
  UseFormReturn,
  SubmitHandler,
  SubmitErrorHandler,
} from "react-hook-form";
import * as yup from "yup";

interface UseFormWithYupProps<
  TFieldValues extends FieldValues,
  TContext,
  Schema
> extends Omit<UseFormProps<TFieldValues, TContext>, "resolver"> {
  validationSchema: yup.Schema<Schema>;
}

type UseFormWithYupHandleSubmit<
  TFieldValues extends FieldValues,
  Schema extends FieldValues
> = (
  onValid: SubmitHandler<Schema>,
  onInvalid?: SubmitErrorHandler<TFieldValues>
) => (e?: React.BaseSyntheticEvent) => Promise<void>;

interface UseFormWithYupReturn<
  TFieldValues extends FieldValues,
  TContext,
  Schema extends FieldValues
> extends Omit<UseFormReturn<TFieldValues, TContext>, "handleSubmit"> {
  handleSubmit: UseFormWithYupHandleSubmit<TFieldValues, Schema>;
}

export default function useFormWithYup<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  Schema extends FieldValues = any
>(
  props: UseFormWithYupProps<TFieldValues, TContext, Schema>
): UseFormWithYupReturn<TFieldValues, TContext, Schema> {
  const { validationSchema, ..._props } = props ?? {};

  const { handleSubmit, ...returned } = useForm({
    ..._props,
    resolver: yupResolver(validationSchema),
  });

  const _handleSubmit: UseFormWithYupHandleSubmit<TFieldValues, Schema> = (
    onValid,
    onInvalid
  ) => {
    const _onValid: SubmitHandler<TFieldValues> = (formValues) =>
      onValid(validationSchema.cast(formValues) as any);
    return handleSubmit(_onValid, onInvalid);
  };

  return { ...returned, handleSubmit: _handleSubmit };
}
